import React, { useState, useEffect } from 'react';
import { ArrowRight, Headphones, Zap, Bot, User, Store, Moon } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

// Conversation data outside component to avoid recreation on every render
const conversation = [
  { type: 'user', text: "Hi! I haven't received my order yet. Order #12345", delay: 500 },
  { type: 'bot', text: "Hi there! Let me check that for you right away. 🔍", delay: 1200, mode: 'offline' },
  { type: 'bot', text: "I found your order! It's currently out for delivery and should arrive by 3 PM today. I've sent the tracking link to your email.", delay: 2000, mode: 'offline' },
  { type: 'user', text: "Perfect! Can I change the delivery address?", delay: 1500 },
  { type: 'bot', text: "Absolutely! I've paused the delivery. What's the new address?", delay: 1800, mode: 'offline' },
  { type: 'user', text: "123 Main St, Apt 4B", delay: 1200 },
  { type: 'bot', text: "✅ Updated! Your order will now be delivered to 123 Main St, Apt 4B by 4:30 PM today. Anything else I can help with?", delay: 2000, mode: 'offline' }
];

const CustomerHappinessAgent = () => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [mode, setMode] = useState('offline'); // 'online' or 'offline'
  const [showModeSwitch, setShowModeSwitch] = useState(false);

  useEffect(() => {
    let timeoutId;
    let currentIndex = 0;

    const addNextMessage = () => {
      if (currentIndex < conversation.length) {
        const currentMessage = conversation[currentIndex];
        
        // Show mode switch notification after first user message
        if (currentIndex === 1) {
          setShowModeSwitch(true);
          timeoutId = setTimeout(() => {
            setShowModeSwitch(false);
            setMode('offline');
            proceedWithMessage();
          }, 2000);
          return;
        }
        
        proceedWithMessage();
        
        function proceedWithMessage() {
          // Show typing indicator
          if (currentMessage.type === 'bot') {
            setIsTyping(true);
            timeoutId = setTimeout(() => {
              setIsTyping(false);
              setMessages(prev => [...prev, currentMessage]);
              currentIndex++;
              timeoutId = setTimeout(addNextMessage, currentMessage.delay);
            }, 1000);
          } else {
            setMessages(prev => [...prev, currentMessage]);
            currentIndex++;
            timeoutId = setTimeout(addNextMessage, currentMessage.delay);
          }
        }
      }
    };

    // Start with online mode
    setMode('online');
    timeoutId = setTimeout(addNextMessage, 800);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50/30">
      <Header />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Customer Support
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Watch AI handle support in real-time—instantly, intelligently, 24/7.
          </p>
        </div>

        {/* Animated Chatbot Demo */}
        <div className="max-w-3xl mx-auto mb-12 md:mb-20">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200 overflow-hidden relative">
            {/* Mode Switch Notification */}
            {showModeSwitch && (
              <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 md:px-6 py-2 md:py-3 rounded-full shadow-2xl animate-fadeIn max-w-[90%]">
                <div className="flex items-center space-x-2">
                  <Moon className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="font-semibold text-xs md:text-sm">Support team offline • AI taking over...</span>
                </div>
              </div>
            )}

            {/* Chat Header */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center space-x-2 md:space-x-3">
                {/* Business Logo */}
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                  <Store className="w-5 h-5 md:w-7 md:h-7 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base flex items-center space-x-2">
                    <span>StyleHub Store</span>
                  </h3>
                  <div className="flex items-center space-x-1.5 md:space-x-2">
                    {mode === 'online' ? (
                      <>
                        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-blue-100 text-xs md:text-sm flex items-center space-x-1">
                          <User className="w-2.5 h-2.5 md:w-3 md:h-3" />
                          <span>Team Online</span>
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-400 rounded-full animate-pulse"></div>
                        <span className="text-blue-100 text-xs md:text-sm flex items-center space-x-1">
                          <Bot className="w-2.5 h-2.5 md:w-3 md:h-3" />
                          <span>AI Assistant</span>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3">
                {mode === 'offline' && (
                  <div className="hidden sm:flex items-center space-x-1 bg-purple-500/30 px-2 md:px-3 py-1 rounded-full">
                    <Moon className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" />
                    <span className="text-white text-xs font-medium">After Hours</span>
                  </div>
                )}
                <div className="text-white text-xs md:text-sm bg-white/20 px-2 md:px-3 py-1 rounded-full backdrop-blur-sm">
                  2s response
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="bg-gradient-to-b from-gray-50 to-white p-4 md:p-6 h-[400px] md:h-[500px] overflow-y-auto">
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex items-start space-x-3 animate-fadeIn ${
                      message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.type === 'user'
                          ? 'bg-gradient-to-br from-purple-400 to-pink-500'
                          : message.mode === 'offline'
                          ? 'bg-gradient-to-br from-purple-400 to-pink-500'
                          : 'bg-gradient-to-br from-blue-400 to-cyan-500'
                      }`}
                    >
                      {message.type === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : message.mode === 'offline' ? (
                        <Bot className="w-4 h-4 text-white" />
                      ) : (
                        <Headphones className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className={message.type === 'user' ? '' : 'flex-1'}>
                      <div
                        className={`px-4 py-3 rounded-2xl shadow-md ${
                          message.type === 'user'
                            ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white max-w-[70%] ml-auto'
                            : 'bg-white text-gray-800 border border-gray-100 max-w-[70%]'
                        }`}
                      >
                        <p className="text-sm leading-relaxed">{message.text}</p>
                      </div>
                      {message.type === 'bot' && message.mode === 'offline' && index === 1 && (
                        <div className="mt-1 ml-4">
                          <span className="text-xs text-purple-600 font-medium flex items-center space-x-1">
                            <Bot className="w-3 h-3" />
                            <span>AI Assistant</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex items-start space-x-3 animate-fadeIn">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      mode === 'offline'
                        ? 'bg-gradient-to-br from-purple-400 to-pink-500'
                        : 'bg-gradient-to-br from-blue-400 to-cyan-500'
                    }`}>
                      {mode === 'offline' ? (
                        <Bot className="w-4 h-4 text-white" />
                      ) : (
                        <Headphones className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="bg-white px-4 py-3 rounded-2xl shadow-md border border-gray-100">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Input (Disabled/Demo) */}
            <div className="bg-gray-50 px-4 md:px-6 py-3 md:py-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 md:space-x-3 bg-white rounded-full px-3 md:px-4 py-2 md:py-3 border border-gray-200">
                <input
                  type="text"
                  placeholder="Watch the AI handle everything..."
                  disabled
                  className="flex-1 outline-none text-gray-400 text-xs md:text-sm bg-transparent"
                />
                <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Badge */}
        <div className="max-w-3xl mx-auto mb-12 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Seamless Handoff</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                When your team is online, they handle support. When they're offline (nights, weekends), 
                AI instantly takes over—same quality, zero wait time. Customers never know the difference.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Why customers love it</h3>
          <p className="text-gray-600">Real results from AI-powered support</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="group text-center bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-pink-400 to-rose-500 rounded-2xl p-8 w-fit mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-white">2s</div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Avg Response Time</h3>
            <p className="text-gray-600">Instant answers, zero wait times</p>
          </div>
          <div className="group text-center bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl p-8 w-fit mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-white">95%</div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Satisfaction Score</h3>
            <p className="text-gray-600">Customers love fast, helpful support</p>
          </div>
          <div className="group text-center bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl p-8 w-fit mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-white">80%</div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Cost Savings</h3>
            <p className="text-gray-600">Reduce support costs dramatically</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-2xl p-16 shadow-xl">
          <h3 className="text-3xl font-bold text-[#2d4a1e] mb-4">
            Ready to transform your support?
          </h3>
          <p className="text-lg text-[#2d4a1e]/80 mb-8 max-w-2xl mx-auto">
            Join companies scaling support with AI—faster, smarter, 24/7
          </p>
          <a
            href="https://calendly.com/kolanut"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-[#2d4a1e] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#2d4a1e]/90 transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
      
      <Footer />
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CustomerHappinessAgent;
